"use client";

import {
  Content as ContentPrimitive,
  DialogDescription,
  DialogTitle,
  Portal as PortalPrimitive,
} from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { mergeRefs, useObjectRef } from "@react-aria/utils";
import { type ComponentProps, type CSSProperties, type ReactNode, useCallback, useMemo, useRef, useState } from "react";

import { cn } from "../../../utils";
import { FloatingPortalContainerContext } from "../../portal/FloatingPortalContainerContext";
import { isToastInteraction } from "../../toast/is-toast-interaction";
import { useDialogRootContext } from "../context";
import Overlay from "../Overlay";
import { BASE_PANE_KEY, type DialogPaneDirection, DialogStackContext, type DialogStackPane } from "./context";
import StackPane from "./StackPane";

export type DialogStackProps = Omit<ComponentProps<typeof ContentPrimitive>, "children"> & {
  children?: ReactNode;
  overlayClassName?: string;
  /** Classes applied to the base pane. */
  paneClassName?: string;
  /** Width classes for the modal box. A pushed `Dialog.Content` can override it with its own `width`. */
  width?: string;
  /** Height ceiling for every pane; a longer pane scrolls its own `Dialog.Body`. */
  maxHeight?: string;
  /** What Escape and click-outside do while a pushed view is open. */
  dismissBehavior?: "back" | "close";
};

interface StackState {
  panes: DialogStackPane[];
  direction: DialogPaneDirection;
}

const initialState: StackState = {
  panes: [{ key: BASE_PANE_KEY, present: true }],
  direction: "initial",
};

const omitKey = <T,>(record: Record<string, T>, key: string): Record<string, T> =>
  key in record ? Object.fromEntries(Object.entries(record).filter(([entryKey]) => entryKey !== key)) : record;

const withoutPane =
  (key: string) =>
  (current: StackState): StackState => ({ ...current, panes: current.panes.filter(pane => pane.key !== key) });

const resolveActiveKey = (panes: DialogStackPane[]) => {
  for (let index = panes.length - 1; index >= 0; index--) {
    const pane = panes[index];
    if (pane?.present) {
      return pane.key;
    }
  }
  return BASE_PANE_KEY;
};

/**
 * A `Dialog.Content` that hosts a stack of views inside one box.
 *
 * Any `Dialog.Root` rendered underneath it becomes a pane instead of a second overlay: opening it
 * slides the next view in and grows the box to its height, closing it slides back. Nothing else
 * about the nested dialog changes, so swapping one in is a matter of wrapping the parent's content.
 */
const Stack = ({
  ref: forwardedRef,
  className,
  overlayClassName,
  paneClassName,
  children,
  width,
  maxHeight = "74vh",
  dismissBehavior = "back",
  onEscapeKeyDown,
  onPointerDownOutside,
  ...props
}: DialogStackProps) => {
  const { contentRef } = useDialogRootContext();
  const ref = useObjectRef(
    useMemo(() => (contentRef ? mergeRefs(forwardedRef, contentRef) : forwardedRef), [contentRef, forwardedRef])
  );

  const [viewport, setViewport] = useState<HTMLDivElement | null>(null);
  const [{ panes, direction }, setState] = useState<StackState>(initialState);
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [widths, setWidths] = useState<Record<string, string | undefined>>({});

  const closersRef = useRef(new Map<string, () => void>());
  const panesRef = useRef(panes);
  panesRef.current = panes;

  const registerPane = useCallback((key: string, requestClose: () => void) => {
    closersRef.current.set(key, requestClose);
    setState(current =>
      current.panes.some(pane => pane.key === key)
        ? current
        : { panes: [...current.panes, { key, present: true }], direction: "forward" }
    );

    return () => {
      closersRef.current.delete(key);
      setState(withoutPane(key));
      setHeights(current => omitKey(current, key));
      setWidths(current => omitKey(current, key));
    };
  }, []);

  const setPanePresent = useCallback((key: string, present: boolean) => {
    setState(current => {
      const pane = current.panes.find(item => item.key === key);
      if (!pane || pane.present === present) {
        return current;
      }
      return {
        panes: current.panes.map(item => (item.key === key ? { ...item, present } : item)),
        direction: present ? "forward" : "backward",
      };
    });
  }, []);

  const setPaneHeight = useCallback((key: string, height: number) => {
    setHeights(current => (current[key] === height ? current : { ...current, [key]: height }));
  }, []);

  const setPaneWidth = useCallback((key: string, paneWidth: string | undefined) => {
    setWidths(current => (current[key] === paneWidth ? current : { ...current, [key]: paneWidth }));
  }, []);

  const back = useCallback(() => {
    const topKey = resolveActiveKey(panesRef.current);
    if (topKey !== BASE_PANE_KEY) {
      closersRef.current.get(topKey)?.();
    }
  }, []);

  const activeKey = useMemo(() => resolveActiveKey(panes), [panes]);
  const canGoBack = activeKey !== BASE_PANE_KEY;

  const stack = useMemo(
    () => ({ panes, activeKey, direction, viewport, registerPane, setPanePresent, setPaneHeight, setPaneWidth, back }),
    [panes, activeKey, direction, viewport, registerPane, setPanePresent, setPaneHeight, setPaneWidth, back]
  );

  const dismissToBack = dismissBehavior === "back" && canGoBack;
  const activeHeight = heights[activeKey];

  return (
    <PortalPrimitive>
      <Overlay className={cn("p-4", overlayClassName)}>
        <VisuallyHidden>
          <DialogTitle />
          <DialogDescription />
        </VisuallyHidden>
        <ContentPrimitive
          ref={ref}
          style={{ "--pd-dialog-pane-max-h": maxHeight } as CSSProperties}
          className={cn(
            "relative top-[13vh] left-[50%] z-50 w-full translate-x-[-50%] overflow-clip rounded-xl bg-white shadow-xl",
            "transition-[max-width] duration-300 ease-in-out motion-reduce:transition-none",
            width ?? "max-w-3xl",
            widths[activeKey],
            className
          )}
          onEscapeKeyDown={event => {
            onEscapeKeyDown?.(event);
            if (!event.defaultPrevented && dismissToBack) {
              event.preventDefault();
              back();
            }
          }}
          onPointerDownOutside={event => {
            if (isToastInteraction(event)) {
              event.preventDefault();
              return;
            }
            onPointerDownOutside?.(event);
            if (!event.defaultPrevented && dismissToBack) {
              event.preventDefault();
              back();
            }
          }}
          {...props}
        >
          <DialogStackContext.Provider value={stack}>
            <div
              ref={setViewport}
              data-dialog-stack-viewport=""
              className="relative overflow-clip transition-[height] duration-300 ease-in-out motion-reduce:transition-none"
              style={{ height: activeHeight || undefined }}
            >
              <StackPane paneKey={BASE_PANE_KEY} present width={width} className={paneClassName}>
                <FloatingPortalContainerContext.Provider value={null}>
                  {children}
                </FloatingPortalContainerContext.Provider>
              </StackPane>
            </div>
          </DialogStackContext.Provider>
        </ContentPrimitive>
      </Overlay>
    </PortalPrimitive>
  );
};

Stack.displayName = "DialogStack";

export default Stack;
